import React from 'react';
import { Camera, Video, Palette, Clock, DollarSign, FileText } from 'lucide-react';

const ProductionPricing = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="production-pricing" className="py-24 px-6 fade-in-section">
      <div className="max-w-7xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-20">
          <h2 className="font-playfair text-5xl md:text-6xl font-bold mb-6">
            Production & <span className="text-gold">Pricing</span>
          </h2>
          <h3 className="text-3xl md:text-4xl font-playfair mb-6">
            Built Around<br />Your Vision.
          </h3>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
            Photography, video, design, and full-scale production — tailored to the needs of your project.
          </p>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Work with me directly or scale with a trusted creative team when the scope calls for it.
          </p>
          <p className="text-sm text-gold mt-8 italic">All pricing represents starting rates.</p>
        </div>

        {/* VIDEO PRODUCTION */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <Video className="text-magenta mx-auto mb-4" size={40} />
            <h3 className="text-3xl font-playfair font-bold mb-4">VIDEO PRODUCTION</h3>
            <p className="text-gray-400 max-w-3xl mx-auto">
              From a single camera to a full production.
            </p>
            <p className="text-gray-400 max-w-3xl mx-auto mt-2">
              Every project is different. Production, deliverables, and creative support are tailored to the scope — from focused individual shoots to larger, fully supported productions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Single-Camera */}
            <div className="bg-zinc-950 p-8 border border-gray-800 hover:border-gold transition-all">
              <h4 className="text-xl font-bold mb-2">SINGLE-CAMERA PRODUCTION</h4>
              <div className="text-4xl font-bold text-gold mb-2">$1,000</div>
              <p className="text-sm text-gray-400 mb-4">Up to 4 Hours</p>
              <p className="text-sm text-gray-300 mb-4">
                A streamlined production for interviews, social content, performances, branded content, and smaller-scale shoots.
              </p>
              <p className="text-sm text-gray-400 mb-4">
                1 Camera · Professional Audio · Basic Lighting
              </p>
              <button onClick={scrollToContact} className="text-magenta border border-magenta px-4 py-2 hover:bg-magenta hover:text-white transition-all w-full">
                INQUIRE
              </button>
            </div>

            {/* Two-Camera */}
            <div className="bg-zinc-950 p-8 border border-gray-800 hover:border-gold transition-all">
              <h4 className="text-xl font-bold mb-2">TWO-CAMERA PRODUCTION</h4>
              <div className="text-4xl font-bold text-gold mb-2">$1,400</div>
              <p className="text-sm text-gray-400 mb-4">Up to 4 Hours</p>
              <p className="text-sm text-gray-300 mb-4">
                Additional coverage and flexibility for interviews, conversations, performances, branded content, and projects that benefit from multiple perspectives.
              </p>
              <p className="text-sm text-gray-400 mb-4">
                2 Cameras · Professional Audio · Lighting
              </p>
              <button onClick={scrollToContact} className="text-magenta border border-magenta px-4 py-2 hover:bg-magenta hover:text-white transition-all w-full">
                INQUIRE
              </button>
            </div>

            {/* Multicam */}
            <div className="bg-zinc-950 p-8 border border-gray-800 hover:border-gold transition-all">
              <h4 className="text-xl font-bold mb-2">MULTICAM PRODUCTION</h4>
              <div className="text-4xl font-bold text-gold mb-2">$1,800</div>
              <p className="text-sm text-gray-400 mb-4">Up to 4 Hours</p>
              <p className="text-sm text-gray-300 mb-4">
                A complete three-camera production for podcasts, interviews, performances, events, and long-form content.
              </p>
              <p className="text-sm text-gray-400 mb-4">
                3 Cameras · Professional Audio · Lighting · Multicam Setup
              </p>
              <button onClick={scrollToContact} className="text-magenta border border-magenta px-4 py-2 hover:bg-magenta hover:text-white transition-all w-full">
                INQUIRE
              </button>
            </div>

            {/* Full Production */}
            <div className="bg-zinc-950 p-8 border-2 border-gold hover:border-magenta transition-all">
              <h4 className="text-xl font-bold mb-2">FULL PRODUCTION</h4>
              <div className="text-4xl font-bold text-gold mb-2">$2,500+</div>
              <p className="text-sm text-gray-400 mb-4">Up to 6 Hours</p>
              <p className="text-sm text-gray-300 mb-4">
                For larger projects requiring additional crew, equipment, planning, and creative oversight.
              </p>
              <p className="text-sm text-gray-400 mb-4">
                Multicam · Creative Direction · Expanded Lighting & Audio · Production Crew as Needed
              </p>
              <button onClick={scrollToContact} className="bg-magenta text-white px-4 py-2 hover:bg-magenta/90 transition-all w-full">
                BUILD A PRODUCTION
              </button>
            </div>
          </div>
        </div>

        {/* EDITING & DELIVERABLES */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <FileText className="text-magenta mx-auto mb-4" size={40} />
            <h3 className="text-3xl font-playfair font-bold mb-4">EDITING & DELIVERABLES</h3>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Turn the footage into content.
            </p>
            <p className="text-gray-400 max-w-3xl mx-auto mt-2">
              Production and post-production are priced separately, giving you the flexibility to choose exactly what you need.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">SHORT-FORM SOCIAL VIDEO</h4>
              <p className="text-2xl text-gold mb-2">Starting at $150</p>
              <p className="text-sm text-gray-400">Polished short-form content built for Instagram, TikTok, YouTube Shorts, and other social platforms.</p>
            </div>

            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">MULTICAM SHORT-FORM VIDEO</h4>
              <p className="text-2xl text-gold mb-2">Starting at $225</p>
              <p className="text-sm text-gray-400">Short-form content edited across multiple camera angles with synchronized audio and polished finishing.</p>
            </div>

            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">5 MULTICAM VIDEOS</h4>
              <p className="text-2xl text-gold mb-2">Starting at $900</p>
              <p className="text-sm text-gray-400">Five finished multicam short-form videos created from your production.</p>
            </div>

            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">10 MULTICAM VIDEOS</h4>
              <p className="text-2xl text-gold mb-2">Starting at $1,500</p>
              <p className="text-sm text-gray-400">A larger content package designed to turn a single production into an ongoing library of social content.</p>
            </div>

            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">HIGHLIGHT / RECAP FILM</h4>
              <p className="text-2xl text-gold mb-2">Starting at $500</p>
              <p className="text-sm text-gray-400">A cinematic edit built around the strongest moments from an event, experience, performance, or activation.</p>
            </div>

            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">LONG-FORM / YOUTUBE EDIT</h4>
              <p className="text-2xl text-gold mb-2">Starting at $500</p>
              <p className="text-sm text-gray-400">A complete long-form edit for YouTube, interviews, branded storytelling, and digital content.</p>
            </div>

            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">MULTICAM LONG-FORM EDIT</h4>
              <p className="text-2xl text-gold mb-2">Starting at $750</p>
              <p className="text-sm text-gray-400">Full long-form post-production incorporating multiple synchronized cameras and professional audio.</p>
            </div>

            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">ADDITIONAL EDITING</h4>
              <p className="text-2xl text-gold mb-2">$100 / Hour</p>
              <p className="text-sm text-gray-400">One round of revisions is included with edited deliverables.</p>
            </div>
          </div>
        </div>

        {/* PRODUCTION ADD-ONS */}
        <div className="mb-24 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-playfair font-bold mb-4">PRODUCTION ADD-ONS</h3>
            <p className="text-gray-400">Scale the production when you need it.</p>
            <p className="text-gray-400 mt-2">Add crew, equipment, and production capabilities based on the needs of your project.</p>
          </div>

          <div className="bg-zinc-950 p-8 border border-gray-800">
            <ul className="space-y-3 text-gray-300">
              <li className="flex justify-between items-center border-b border-gray-800 pb-3">
                <span>Additional Camera</span>
                <span className="text-gold">Starting at +$400</span>
              </li>
              <li className="flex justify-between items-center border-b border-gray-800 pb-3">
                <span>Additional Camera Operator</span>
                <span className="text-gold">Starting at +$600</span>
              </li>
              <li className="flex justify-between items-center border-b border-gray-800 pb-3">
                <span>Drone Coverage</span>
                <span className="text-gold">Starting at +$400</span>
              </li>
              <li className="flex justify-between items-center border-b border-gray-800 pb-3">
                <span>Dedicated Audio Operator</span>
                <span className="text-gold">Starting at +$500</span>
              </li>
              <li className="flex justify-between items-center border-b border-gray-800 pb-3">
                <span>Additional Production Time</span>
                <span className="text-gold">Starting at +$200 / hour</span>
              </li>
              <li className="flex justify-between items-center border-b border-gray-800 pb-3">
                <span>Specialty Equipment & Crew</span>
                <span className="text-gold">Custom Quote</span>
              </li>
              <li className="flex justify-between items-center">
                <span>Rush Turnaround</span>
                <span className="text-gold">+25–50%</span>
              </li>
            </ul>
          </div>
        </div>

        {/* PHOTOGRAPHY */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <Camera className="text-magenta mx-auto mb-4" size={40} />
            <h3 className="text-3xl font-playfair font-bold mb-4">PHOTOGRAPHY</h3>
            <p className="text-gray-400">Still imagery with the same creative approach.</p>
            <p className="text-gray-400 mt-2">Photography for brands, events, artists, portraits, campaigns, lifestyle, and social content.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-zinc-950 p-8 border border-gray-800 hover:border-gold transition-all">
              <h4 className="text-xl font-bold mb-2">HALF-DAY PRODUCTION</h4>
              <div className="text-4xl font-bold text-gold mb-2">$750</div>
              <p className="text-sm text-gray-400 mb-4">Up to 4 Hours</p>
              <p className="text-sm text-gray-300 mb-6">Professional photography coverage with a curated and edited final gallery.</p>
              <div className="text-sm text-gray-400 space-y-2">
                <p>Additional Coverage — $200 / hour</p>
                <p>Advanced Retouching — $75 / hour</p>
                <p className="text-xs italic mt-4">Final image count is determined by the scope and nature of the project.</p>
              </div>
            </div>

            <div className="bg-zinc-950 p-8 border border-gray-800 hover:border-gold transition-all">
              <h4 className="text-xl font-bold mb-2">FULL-DAY PRODUCTION</h4>
              <div className="text-4xl font-bold text-gold mb-2">$1,400</div>
              <p className="text-sm text-gray-400 mb-4">Up to 8 Hours</p>
              <p className="text-sm text-gray-300 mb-6">Extended photography coverage for larger events, campaigns, productions, and content days.</p>
              <button onClick={scrollToContact} className="text-magenta border border-magenta px-6 py-2 hover:bg-magenta hover:text-white transition-all w-full mt-8">
                BOOK PHOTOGRAPHY
              </button>
            </div>
          </div>
        </div>

        {/* GRAPHIC DESIGN */}
        <div className="mb-24 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Palette className="text-magenta mx-auto mb-4" size={40} />
            <h3 className="text-3xl font-playfair font-bold mb-4">GRAPHIC DESIGN</h3>
            <p className="text-gray-400">Design that completes the picture.</p>
            <p className="text-gray-400 mt-2">Standalone creative or design support incorporated into a larger photo, video, or content campaign.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">Individual Digital Asset</h4>
              <p className="text-2xl text-gold">Starting at $150</p>
            </div>
            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">Social Content Package</h4>
              <p className="text-2xl text-gold">Starting at $400</p>
            </div>
            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">Campaign Creative</h4>
              <p className="text-2xl text-gold">Starting at $750</p>
            </div>
            <div className="bg-zinc-950 p-6 border border-gray-800">
              <h4 className="font-bold mb-2">Ongoing Design & Creative Support</h4>
              <p className="text-2xl text-gold">$100 / hour</p>
            </div>
          </div>
          <div className="text-center mt-6">
            <button onClick={scrollToContact} className="text-magenta border border-magenta px-6 py-3 hover:bg-magenta hover:text-white transition-all">
              DISCUSS A PROJECT
            </button>
          </div>
        </div>

        {/* USAGE & LICENSING */}
        <div className="mb-24 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-playfair font-bold mb-4">USAGE & LICENSING</h3>
            <p className="text-gray-400">Your content, ready to be used.</p>
          </div>

          <div className="bg-zinc-950 p-8 border border-gray-800">
            <h4 className="font-bold text-gold mb-4">STANDARD USAGE — INCLUDED</h4>
            <p className="text-gray-300 mb-8">
              Website · Organic Social Media · YouTube · Internal Marketing · General Digital Use
            </p>
            
            <h4 className="font-bold text-gold mb-4">COMMERCIAL USAGE</h4>
            <p className="text-gray-300">
              Paid advertising, broadcast, third-party licensing, resale, and larger commercial campaigns are quoted separately based on usage, duration, platforms, and reach.
            </p>
          </div>
        </div>

        {/* CUSTOM PRODUCTIONS */}
        <div className="mb-16 max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-zinc-950 to-zinc-900 p-12 border-2 border-gold text-center">
            <h3 className="text-3xl font-playfair font-bold mb-4">CUSTOM PRODUCTIONS</h3>
            <p className="text-xl text-gray-300 mb-4">Need more than one service?</p>
            <p className="text-gray-400 mb-6">
              Photography + Video · Multicam + Drone · Long-Form + Social · Design + Production
            </p>
            <p className="text-gray-300 mb-6">
              Services can be combined into a custom production built specifically around your project.
            </p>
            <p className="text-gray-300 mb-8">
              Whether you need one creator or a larger team, I'll build the right approach around your scope, deliverables, and creative vision.
            </p>
            <h4 className="text-2xl font-bold mb-4 text-gold">Have something in mind?</h4>
            <p className="text-gray-300 mb-6">
              Tell me what you're creating, what you need delivered, and where the content will live.<br />
              I'll take it from there.
            </p>
            <button onClick={scrollToContact} className="bg-magenta text-white px-8 py-4 hover:bg-magenta/90 transition-all text-lg">
              GET IN TOUCH
            </button>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center text-xs text-gray-500 italic max-w-4xl mx-auto leading-relaxed">
          Pricing shown represents starting rates and may vary based on project scope, production time, crew requirements, equipment, location, travel, deliverables, editing complexity, usage, and turnaround. Final pricing is provided once project scope and deliverables are confirmed.
        </div>
      </div>
    </section>
  );
};

export default ProductionPricing;
